import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { acceptInvite, fetchInvite, requestPasswordReset } from "../lib/auth-api";
import { useAuth } from "../lib/auth-session";
import { PageHeader, PrimaryButton, Surface, Tag } from "../components/primitives";
import { PageSkeleton } from "../components/PageSkeleton";

export function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="nl-auth-page">
      <div className="nl-auth-card">
        <div className="nl-auth-brand">
          <img src="/mark.svg" alt="" aria-hidden width={26} height={26} />
          <div>
            <div className="nl-auth-brand-name">Altibbi Newsletter</div>
            <div className="nl-auth-brand-sub">Workspace</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function LoginPage() {
  const { ready, session, login, bootstrap } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || "/";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetNote, setResetNote] = useState<string | null>(null);

  const onForgot = async () => {
    setError(null);
    if (!email.trim()) {
      setError("Type your email address first, then ask for a reset link.");
      return;
    }
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      // Deliberately the same words whether or not the address has an account.
      setResetNote(`If ${email.trim()} has an account, a link to choose a new password is on its way. It lasts an hour.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not ask for a reset link.");
    } finally {
      setBusy(false);
    }
  };
  const setup = session.configured && !session.has_users;

  if (!ready) return <PageSkeleton />;
  if (session.me) return <Navigate to={from} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (setup) await bootstrap({ name, email, password });
      else await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  }

  if (!session.configured) {
    return (
      <AuthCard>
        <PageHeader title="Sign in" meta="Restart the Worker so team login can load." />
        <Surface>
          <div style={{ padding: "var(--ui-space-4)" }}>
            <p style={{ margin: 0, color: "var(--c-t2)", fontSize: "var(--ui-text-body)", lineHeight: 1.45 }}>
              Team accounts live on the Worker. Restart <code>wrangler dev</code> on port 8787, then refresh this page.
            </p>
          </div>
        </Surface>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <PageHeader
        title={setup ? "Create the first admin" : "Sign in"}
        meta={setup
          ? "No one is on the team yet. This account can invite everyone else."
          : "Use the email you were invited with. If you have not set a password yet, open the link in your invitation — or ask for a new one below."}
      />
      <Surface>
        <form className="nl-form" style={{ padding: "var(--ui-space-4)" }} onSubmit={onSubmit}>
          {setup && (
            <label className="nl-field">
              <span>Name</span>
              <input className="nl-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoComplete="name" />
            </label>
          )}
          <label className="nl-field">
            <span>Email</span>
            <input className="nl-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
          </label>
          <label className="nl-field">
            <span>Password</span>
            <input className="nl-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={setup ? "new-password" : "current-password"} />
          </label>
          {error && <p className="nl-auth-error" role="alert">{error}</p>}
          {resetNote && <p className="nl-auth-note" role="status">{resetNote}</p>}
          <div className="nl-action-row" style={{ justifyContent: setup ? "flex-end" : "space-between" }}>
            {!setup && (
              <button type="button" className="nl-text-btn" disabled={busy} onClick={onForgot}>
                Forgot your password?
              </button>
            )}
            <PrimaryButton type="submit" disabled={busy}>{busy ? "Please wait…" : setup ? "Create admin" : "Sign in"}</PrimaryButton>
          </div>
        </form>
      </Surface>
    </AuthCard>
  );
}

export function InvitePage({ token }: { token: string }) {
  const { session, refresh } = useAuth();
  const [info, setInfo] = useState<{ email: string; name: string; role: string; returning?: boolean } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    fetchInvite(token)
      .then(setInfo)
      .catch((err: Error) => setLoadError(err.message));
  }, [token]);

  if (session.me && info) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await acceptInvite(token, password);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not set password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard>
      <PageHeader
        title={info?.returning ? "Choose a new password" : "You’re invited"}
        meta={info ? (
          <>
            <span>{info.name}</span>
            <span>{info.email}</span>
            <Tag tone={info.role === "admin" ? "accent" : info.role === "editor" ? "purple" : "teal"}>
              {info.role.charAt(0).toUpperCase() + info.role.slice(1)}
            </Tag>
          </>
        ) : "Open your invitation to join the workspace."}
      />
      <Surface>
        {loadError ? (
          <div style={{ padding: "var(--ui-space-4)", display: "grid", gap: "var(--ui-space-3)" }}>
            <p className="nl-auth-error" role="alert">{loadError}</p>
            <Link to="/login" style={{ color: "var(--c-accent)", fontWeight: 600 }}>Go to sign in</Link>
          </div>
        ) : (
          <form className="nl-form" style={{ padding: "var(--ui-space-4)" }} onSubmit={onSubmit}>
            <p style={{ margin: 0, fontSize: "var(--ui-text-body)", lineHeight: 1.45, color: "var(--c-t2)" }}>
              Set a password to sign in. This link expires in 7 days.
            </p>
            <label className="nl-field">
              <span>New password</span>
              <input className="nl-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            </label>
            <label className="nl-field">
              <span>Confirm password</span>
              <input className="nl-input" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </label>
            {error && <p className="nl-auth-error" role="alert">{error}</p>}
            <div className="nl-action-row" style={{ justifyContent: "flex-end" }}>
              <PrimaryButton type="submit" disabled={busy || !info}>{busy ? "Saving…" : "Set password and join"}</PrimaryButton>
            </div>
          </form>
        )}
      </Surface>
    </AuthCard>
  );
}
